import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const CATEGORIES = [
  '', 'Technology', 'Business', 'Finance', 'Health', 'Science',
  'Education', 'Entertainment', 'Sports', 'Environment', 'Politics',
];

export default function AddPressRelease() {
  const [form, setForm] = useState({
    headline: '', summary: '', body: '', category: '', tags: '',
    companyName: '', contactPerson: '', email: '', phone: '', website: '',
    city: '', state: '', country: '',
    metaTitle: '', metaDescription: '', keywords: '',
  });
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, getIdToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    const token = getIdToken();
    axios.get(`${API_URL}/api/companies.json`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setCompanies(res.data.companies || []))
      .catch(() => {});
  }, [currentUser, getIdToken]);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  function handleCompanySelect(e) {
    const id = e.target.value;
    setSelectedCompanyId(id);
    if (!id) {
      setForm((f) => ({ ...f, companyName: '', contactPerson: '', email: '', phone: '', website: '', city: '', state: '', country: '' }));
      return;
    }
    const c = companies.find((c) => String(c.id) === id);
    if (c) {
      setForm((f) => ({
        ...f,
        companyName: c.companyName || '', contactPerson: c.contactPerson || '',
        email: c.email || '', phone: c.phone || '', website: c.website || '',
        city: c.city || '', state: c.state || '', country: c.country || '',
      }));
    }
  }

  async function handleSubmit(e, submitStatus) {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.headline.trim() || !form.body.trim()) {
      return setError('Headline and body are required.');
    }
    setLoading(true);
    try {
      const token = getIdToken();
      await axios.post(
        `${API_URL}/api/press-releases.json`,
        { ...form, status: submitStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(submitStatus === 'published' ? 'Press release published!' : 'Saved as draft.');
      setTimeout(() => navigate('/dashboard/press-releases'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save press release.');
    }
    setLoading(false);
  }

  return (
    <div>
      <h2 style={s.pageTitle}>Add Press Release</h2>

      {error && <div style={s.error}>{error}</div>}
      {success && <div style={s.successMsg}>{success}</div>}

      <div style={s.card}>
        <form style={s.form}>
          <Section label="Press Release Content">
            <Field label="Headline *">
              <input style={s.input} type="text" value={form.headline} onChange={set('headline')} placeholder="Your press release headline..." />
            </Field>
            <Field label="Summary">
              <textarea style={{ ...s.input, resize: 'vertical' }} rows={3} value={form.summary} onChange={set('summary')} placeholder="A brief one-paragraph summary..." />
            </Field>
            <Field label="Body *">
              <textarea style={{ ...s.input, resize: 'vertical', lineHeight: 1.7 }} rows={16} value={form.body} onChange={set('body')} placeholder="Write the full press release here..." />
            </Field>
          </Section>

          <Section label="Category & Tags">
            <div style={s.twoCol}>
              <Field label="Category">
                <select style={s.input} value={form.category} onChange={set('category')}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c || '— Select a category —'}</option>)}
                </select>
              </Field>
              <Field label="Tags">
                <input style={s.input} type="text" value={form.tags} onChange={set('tags')} placeholder="e.g. AI, funding, product launch" />
              </Field>
            </div>
          </Section>

          <Section label="Company & Contact">
            {companies.length > 0 && (
              <Field label="Auto-fill from saved company">
                <select style={s.input} value={selectedCompanyId} onChange={handleCompanySelect}>
                  <option value="">— Select a company —</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName || 'Unnamed Company'}</option>)}
                </select>
              </Field>
            )}
            <div style={s.twoCol}>
              <Field label="Company Name"><input style={s.input} type="text" value={form.companyName} onChange={set('companyName')} placeholder="Acme Corp" /></Field>
              <Field label="Contact Person"><input style={s.input} type="text" value={form.contactPerson} onChange={set('contactPerson')} placeholder="Full name" /></Field>
            </div>
            <div style={s.twoCol}>
              <Field label="Email"><input style={s.input} type="email" value={form.email} onChange={set('email')} placeholder="press@company.com" /></Field>
              <Field label="Phone"><input style={s.input} type="tel" value={form.phone} onChange={set('phone')} placeholder="+1 555 000 0000" /></Field>
            </div>
            <Field label="Website"><input style={s.input} type="url" value={form.website} onChange={set('website')} placeholder="https://yourcompany.com" /></Field>
            <div style={s.threeCol}>
              <Field label="City"><input style={s.input} type="text" value={form.city} onChange={set('city')} placeholder="New York" /></Field>
              <Field label="State"><input style={s.input} type="text" value={form.state} onChange={set('state')} placeholder="NY" /></Field>
              <Field label="Country"><input style={s.input} type="text" value={form.country} onChange={set('country')} placeholder="USA" /></Field>
            </div>
          </Section>

          <Section label="SEO">
            <Field label="Meta Title"><input style={s.input} type="text" value={form.metaTitle} onChange={set('metaTitle')} placeholder="SEO title (defaults to headline if blank)" /></Field>
            <Field label="Meta Description"><textarea style={{ ...s.input, resize: 'vertical' }} rows={2} value={form.metaDescription} onChange={set('metaDescription')} placeholder="~160 chars for search engines" /></Field>
            <Field label="Keywords"><input style={s.input} type="text" value={form.keywords} onChange={set('keywords')} placeholder="Comma-separated keywords" /></Field>
          </Section>

          <div style={s.actions}>
            <button type="button" disabled={loading} style={s.btnOutline} onClick={(e) => handleSubmit(e, 'draft')}>
              {loading ? 'Saving...' : 'Save as Draft'}
            </button>
            <button type="submit" disabled={loading} style={s.btnPrimary} onClick={(e) => handleSubmit(e, 'published')}>
              {loading ? 'Publishing...' : 'Publish Now'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid #f0f0f0' }}>
      <h3 style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#999', margin: '0 0 1rem' }}>{label}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#444', marginBottom: '0.3rem' }}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  pageTitle: { margin: '0 0 1.5rem', fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e' },
  error: { background: '#fee', border: '1px solid #fcc', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', color: '#c00', fontSize: '0.9rem' },
  successMsg: { background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem', color: '#15803d', fontSize: '0.9rem' },
  card: { background: '#fff', borderRadius: 10, padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' },
  form: { display: 'flex', flexDirection: 'column' },
  input: { width: '100%', padding: '0.65rem 0.9rem', borderRadius: 6, border: '1px solid #ddd', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  threeCol: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' },
  actions: { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  btnPrimary: { padding: '0.75rem 1.75rem', background: '#e94560', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  btnOutline: { padding: '0.75rem 1.75rem', background: 'transparent', color: '#555', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
};
